// src/pages/ELearning/ELearningDashboard.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getTests, getPersonalAnalytics } from '../../api/client';
import { getMandatoryStatus } from '../../api/client';
import { Award, TrendingUp, Clock, Star, BookOpen, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';

interface Test {
  _id: string;
  title: string;
  description: string;
  image?: string;
  category: string;
  timeLimit: number;
  points: number;
  questionsCount?: number;
}

const ELearningDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState<Test[]>([]);
  const [ratingHistory, setRatingHistory] = useState<any[]>([]);
  const [stats, setStats] = useState({ completed: 0, avgScore: 0, totalTime: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mandatory, setMandatory] = useState<any>(null);
  const [showPendingDropdown, setShowPendingDropdown] = useState(false);

  // ─── ФИЧА 11: Mandatory статус ───
  useEffect(() => {
    getMandatoryStatus()
      .then((d: any) => setMandatory(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testsRes, analyticsRes] = await Promise.all([
          getTests({ active: true, limit: 3 }),
          getPersonalAnalytics(),
        ]);
        setTests(testsRes);
        setRatingHistory(analyticsRes.ratingHistory || []);
        setStats(analyticsRes.stats || { completed: 0, avgScore: 0, totalTime: 0 });
      } catch (err: any) {
        console.error('Ошибка загрузки данных:', err);
        setError(err.response?.data?.error || 'Не удалось загрузить данные. Попробуйте позже.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNavigateToTest = (testId: string) => {
    navigate(`/app/elearning/tests/${testId}`);
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#2575fc' }} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ff4d4f" />
          <h2 style={{ marginTop: 20 }}>{error}</h2>
          <button onClick={() => window.location.reload()} style={styles.retryButton}>Повторить</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={styles.header}>
        <h1 style={styles.title}>Добро пожаловать, {user?.name?.split(' ')[0] || 'пользователь'}!</h1>

      {/* ─── ФИЧА 11: Обязательные тесты баннер ─── */}
      {mandatory?.blocked && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(231,76,60,0.15), rgba(245,158,11,0.15))',
          border: '2px solid #e74c3c',
          borderRadius: 16,
          padding: 20,
          marginBottom: 24,
          display: 'flex',
          gap: 16,
          alignItems: 'center',
        }}>
          <AlertTriangle size={32} color="#e74c3c" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#e74c3c', marginBottom: 4 }}>
              🔒 У вас есть обязательные тесты
            </div>
            <div style={{ fontSize: 13, color: '#666' }}>
              Не пройдено: {mandatory.pendingTests?.length || 0} из {mandatory.total}
              {mandatory.pendingTests && mandatory.pendingTests.length > 0 && (
                <span> — «{mandatory.pendingTests[0].title}»{mandatory.pendingTests.length > 1 ? ' и ещё ' + (mandatory.pendingTests.length - 1) : ''}</span>
              )}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowPendingDropdown(v => !v)}
              style={{
                padding: '10px 20px',
                background: '#e74c3c',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Пройти сейчас ▾
            </button>

            {showPendingDropdown && (mandatory?.pendingTests?.length || 0) > 0 && (
              <>
                {/* клик вне — закрыть */}
                <div
                  onClick={() => setShowPendingDropdown(false)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 99,
                    background: 'transparent',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 8,
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    minWidth: 300,
                    maxWidth: 400,
                    zIndex: 100,
                    padding: 8,
                    border: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  {mandatory.pendingTests.map((t: any) => (
                    <Link
                      key={t._id}
                      to={`/app/elearning/tests/${t._id}`}
                      onClick={() => setShowPendingDropdown(false)}
                      style={{
                        display: 'block',
                        padding: '10px 14px',
                        borderRadius: 8,
                        color: '#333',
                        textDecoration: 'none',
                        fontSize: 14,
                        fontWeight: 500,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(231,76,60,0.08)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      🎯 {t.title}
                    </Link>
                  ))}
                  <div style={{
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    marginTop: 4,
                    paddingTop: 8,
                    paddingBottom: 4,
                  }}>
                    <Link
                      to="/app/elearning/my-assignments"
                      onClick={() => setShowPendingDropdown(false)}
                      style={{
                        display: 'block',
                        padding: '8px 14px',
                        color: '#e74c3c',
                        textDecoration: 'none',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      Все мои назначения →
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
        <p style={styles.subtitle}>Продолжайте учиться и зарабатывайте бенефиты</p>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <Award size={28} color="#f1c40f" />
          <div>
            <div style={styles.statValue}>{user?.elearning?.rating ?? 0}</div>
            <div style={styles.statLabel}>Ваш рейтинг</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <TrendingUp size={28} color="#2ecc71" />
          <div>
            <div style={styles.statValue}>{stats.completed}</div>
            <div style={styles.statLabel}>тестов пройдено</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <Clock size={28} color="#3498db" />
          <div>
            <div style={styles.statValue}>{Math.round(stats.totalTime / 60)}ч</div>
            <div style={styles.statLabel}>обучения</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <Star size={28} color="#e67e22" />
          <div>
            <div style={styles.statValue}>{stats.avgScore.toFixed(1)}%</div>
            <div style={styles.statLabel}>средний балл</div>
          </div>
        </div>
      </div>

      <div style={styles.chartCard}>
        <h3 style={styles.chartTitle}>Динамика рейтинга</h3>
        {ratingHistory.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={ratingHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#666' }} />
              <YAxis tick={{ fill: '#666' }} />
              <Tooltip />
              <Line type="monotone" dataKey="rating" stroke="#8e44ad" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: '#999' }}>Пока нет данных. Пройдите первый тест!</p>
        )}
      </div>

      <h2 style={styles.sectionTitle}>Рекомендуемые тесты</h2>
      {tests.length === 0 ? (
        <p style={{ color: '#999' }}>Нет доступных тестов. Загляните позже!</p>
      ) : (
        <div style={styles.testGrid}>
          {tests.map(test => (
            <div key={test._id} style={styles.testCard} onClick={() => handleNavigateToTest(test._id)}>
              <img src={test.image || '/images/default-test.jpg'} alt={test.title} style={styles.testImage} />
              <div style={styles.testInfo}>
                <h4 style={styles.testTitle}>{test.title}</h4>
                <p style={styles.testDesc}>{test.description}</p>
                <div style={styles.testMeta}>
                  <span><BookOpen size={14} /> {test.category}</span>
                  <span>⏱ {test.timeLimit || 'Без ограничений'}</span>
                  {test.questionsCount && <span>📋 {test.questionsCount} вопр.</span>}
                </div>
                <button style={styles.startButton}>Начать</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 30 },
  title: { fontSize: 28, fontWeight: 700, marginBottom: 8, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  subtitle: { fontSize: 16, color: '#666' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 30 },
  statCard: { background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 16 },
  statValue: { fontSize: 28, fontWeight: 700, color: '#1a1a2e' },
  statLabel: { fontSize: 14, color: '#666' },
  chartCard: { background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.3)', marginBottom: 30 },
  chartTitle: { fontSize: 18, marginBottom: 16, color: '#1a1a2e' },
  sectionTitle: { fontSize: 22, marginBottom: 20, color: '#1a1a2e' },
  testGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 },
  testCard: { background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)', borderRadius: 24, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 8px 20px rgba(0,0,0,0.05)', transition: 'transform 0.2s', cursor: 'pointer' },
  testImage: { width: '100%', height: 140, objectFit: 'cover' },
  testInfo: { padding: 16 },
  testTitle: { fontSize: 18, marginBottom: 6, color: '#1a1a2e' },
  testDesc: { fontSize: 14, color: '#666', marginBottom: 12 },
  testMeta: { display: 'flex', gap: 16, fontSize: 13, color: '#888', marginBottom: 16, flexWrap: 'wrap' },
  startButton: { background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: 30, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, width: '100%' },
  retryButton: { marginTop: 20, padding: '10px 24px', background: '#2575fc', color: 'white', border: 'none', borderRadius: 30, cursor: 'pointer', fontWeight: 600 },
};

export default ELearningDashboard;